import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { PageHeader, Card, Modal, Field, Btn, Pill, Empty, Notice, inputCls } from "@/dashboard/ui";
import { Megaphone, Pencil, Trash2, Plus, FileText, CalendarDays, Sparkles, Send, Clock, ImagePlus, Film, XCircle } from "lucide-react";
import TasksPanel from "@/dashboard/TasksPanel";
import StrategyPanel from "@/dashboard/StrategyPanel";
import ContentPlanPanel, { EditDraftModal } from "@/dashboard/ContentPlanPanel";
import SocialAccountsPanel from "@/dashboard/SocialAccountsPanel";
import SEOPanel from "@/dashboard/SEOPanel";
import LandingPagesPanel from "@/dashboard/LandingPagesPanel";
import EmailPanel from "@/dashboard/EmailPanel";
import WhatsAppPanel from "@/dashboard/WhatsAppPanel";
import SequencesPanel from "@/dashboard/SequencesPanel";
import AdvertisingPanel from "@/dashboard/AdvertisingPanel";
import StudioAnalyticsPanel from "@/dashboard/AnalyticsPanel";

const TABS = [
  { id: "strategy", label: "AI Strategy" },
  { id: "plan", label: "30-Day Content Plan" },
  { id: "social", label: "Social Media" },
  { id: "email", label: "Email Marketing" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "ads", label: "Advertising" },
  { id: "campaigns", label: "Campaigns" },
  { id: "calendar", label: "Content Calendar" },
  { id: "seo", label: "SEO" },
  { id: "landing-pages", label: "Landing Pages" },
  { id: "analytics-studio", label: "Insights" },
  { id: "social-accounts", label: "Social Accounts" },
  { id: "followups", label: "Lead Follow-ups" },
  { id: "analytics", label: "Analytics" },
];
const EMPTY_DRAFT = { title: "", content: "", email_subject: "", recipient_lead_ids: [], platforms: [], scheduled_at: "", timezone: "", status: "Draft", media: [] };
const SOCIAL_PLATFORMS = ["Instagram", "Facebook", "LinkedIn", "X"];
// Which media file extensions each platform accepts (images / videos).
const MEDIA_COMPAT = {
  Instagram: ["jpg", "png", "mp4", "mov"],
  Facebook: ["jpg", "png", "webp", "mp4", "mov"],
  LinkedIn: ["jpg", "png", "mp4"],
  X: ["jpg", "png", "webp", "mp4"],
};

function MediaThumb({ media }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let objectUrl = null;
    if (media.kind === "image") {
      api.get(`/marketing/media/${media.id}`, { responseType: "blob" })
        .then((r) => { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); })
        .catch(() => setUrl(null));
    }
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [media.id, media.kind]);
  if (media.kind === "video") {
    return (
      <div className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-xl border border-neutral-200 bg-neutral-50 text-neutral-500">
        <Film size={18} />
        <span className="text-[9px] font-bold uppercase">{media.ext}</span>
      </div>
    );
  }
  return url ? (
    <img src={url} alt={media.filename} className="h-20 w-20 rounded-xl border border-neutral-200 object-cover" />
  ) : (
    <div className="h-20 w-20 animate-pulse rounded-xl bg-neutral-100" />
  );
}

function MediaPicker({ media, platforms, onChange }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upload = async (files) => {
    setErr("");
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/marketing/media", fd);
        onChange([...media, data]);
      }
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeMedia = async (m) => {
    try {
      await api.delete(`/marketing/media/${m.id}`);
      onChange(media.filter((x) => x.id !== m.id));
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  // Warn when an attached file's format isn't supported by a selected platform.
  const warnings = [];
  for (const p of platforms) {
    const bad = media.filter((m) => !(MEDIA_COMPAT[p] || []).includes(m.ext));
    for (const m of bad) {
      warnings.push(`${p} doesn't support .${m.ext} ${m.kind === "video" ? "videos" : "images"} (${m.filename})`);
    }
  }

  return (
    <div data-testid="media-picker">
      <div className="flex flex-wrap items-center gap-2.5">
        <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-neutral-300 px-4 py-1.5 text-xs font-semibold text-neutral-600 hover:border-[#111111] hover:text-[#111111] ${busy ? "pointer-events-none opacity-60" : ""}`}
          data-testid="media-upload-button">
          <ImagePlus size={14} /> {busy ? "Uploading…" : "Upload Media"}
          <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.mp4,.mov" className="hidden"
            data-testid="media-upload-input" disabled={busy}
            onChange={(e) => { const fs = Array.from(e.target.files || []); if (fs.length) upload(fs); e.target.value = ""; }} />
        </label>
        <span className="text-[11px] text-neutral-400">JPG, PNG, WebP · MP4, MOV · images up to 10 MB, videos up to 100 MB · add several images for a carousel</span>
      </div>
      {err && <p className="mt-2 text-xs text-red-600" data-testid="media-upload-error">{err}</p>}
      {media.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2.5" data-testid="media-preview-list">
          {media.map((m) => (
            <div key={m.id} className="group relative" data-testid={`media-preview-${m.id}`}>
              <MediaThumb media={m} />
              <button type="button" title={`Remove ${m.filename}`} data-testid={`media-remove-${m.id}`}
                onClick={() => removeMedia(m)}
                className="absolute -right-1.5 -top-1.5 rounded-full bg-white text-neutral-400 shadow hover:text-red-600">
                <XCircle size={18} />
              </button>
              <p className="mt-1 w-20 truncate text-center text-[10px] text-neutral-400">{m.filename}</p>
            </div>
          ))}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5" data-testid="media-compat-warning">
          <p className="text-xs font-semibold text-amber-900">Format not supported everywhere:</p>
          {warnings.map((w, i) => <p key={i} className="mt-0.5 text-[11px] text-amber-800">{w}</p>)}
        </div>
      )}
    </div>
  );
}

const DEVICE_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const EMPTY_CAMPAIGN = { name: "", objective: "", audience: "", channel: "Email", content: "", scheduled_date: "", status: "Draft" };

function AiWriter({ section, onUse }) {
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const generate = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/marketing/generate", { section, topic });
      onUse(data.content);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50/70 p-3.5" data-testid={`ai-writer-${section}`}>
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-neutral-500">
        <Sparkles size={12} /> Write with AI
      </p>
      <div className="flex gap-2">
        <input
          data-testid={`ai-topic-${section}`}
          className={inputCls}
          placeholder="Brief, e.g. spring offer for existing clients"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
        />
        <Btn type="button" data-testid={`ai-generate-${section}`} onClick={generate} disabled={busy || !topic.trim()}>
          {busy ? "Writing…" : "Generate"}
        </Btn>
      </div>
      {err && <p className="mt-2 text-xs text-red-600">{err}</p>}
      <p className="mt-2 text-[11px] text-neutral-400">AI-drafted — review before use. It never invents statistics or claims.</p>
    </div>
  );
}

export function RecipientPicker({ selected, onChange }) {
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => setLeads([]));
  }, []);

  useEffect(() => {
    if (!leads) return;
    const valid = new Set(leads.map((l) => l.id));
    const pruned = selected.filter((id) => valid.has(id));
    if (pruned.length !== selected.length) onChange(pruned);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads]);

  const toggle = (id) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  if (leads === null) return <p className="text-xs text-neutral-400">Loading your leads…</p>;
  if (leads.length === 0) {
    return (
      <p className="rounded-xl bg-neutral-50 px-4 py-3 text-xs text-neutral-500" data-testid="recipient-picker-empty">
        You have no CRM leads yet. Add leads in the CRM Leads section first, then choose recipients here.
      </p>
    );
  }

  const eligible = leads.filter((l) => l.email && l.can_email !== false);
  const allSelected = eligible.length > 0 && eligible.every((l) => selected.includes(l.id));
  const toggleAll = () =>
    onChange(
      allSelected
        ? selected.filter((id) => !eligible.some((l) => l.id === id))
        : [...new Set([...selected, ...eligible.map((l) => l.id)])]
    );

  return (
    <div data-testid="recipient-picker">
      {eligible.length > 0 && (
        <label
          data-testid="recipient-select-all"
          className="mb-1 flex cursor-pointer items-center gap-2.5 rounded-lg bg-neutral-100 px-3 py-2 text-xs font-bold text-[#111111] hover:bg-neutral-200/70"
        >
          <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-3.5 w-3.5 accent-[#111111]" />
          Select all eligible contacts ({eligible.length})
        </label>
      )}
      <div className="max-h-44 space-y-1 overflow-y-auto rounded-xl border border-neutral-200 p-2">
        {leads.map((l) => {
          const noEmail = !l.email;
          const optedOut = !noEmail && l.can_email === false;
          const disabled = noEmail || optedOut;
          return (
            <label
              key={l.id}
              data-testid={`recipient-option-${l.id}`}
              className={`flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-xs ${
                disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-neutral-50"
              }`}
            >
              <input
                type="checkbox"
                disabled={disabled}
                checked={selected.includes(l.id)}
                onChange={() => toggle(l.id)}
                className="h-3.5 w-3.5 accent-[#111111]"
              />
              <span className="font-semibold text-[#111111]">{l.full_name}</span>
              {l.email ? (
                <span className="truncate text-neutral-400">{l.email}</span>
              ) : (
                <span className="text-neutral-400 italic">no email on record</span>
              )}
              {noEmail && (
                <span data-testid={`recipient-badge-noemail-${l.id}`} className="ml-auto shrink-0 rounded-full border border-neutral-300 px-2 py-0.5 text-[10px] font-semibold text-neutral-500">
                  No email address
                </span>
              )}
              {optedOut && (
                <span data-testid={`recipient-badge-optout-${l.id}`} className="ml-auto shrink-0 rounded-full border border-neutral-300 px-2 py-0.5 text-[10px] font-semibold text-neutral-500">
                  Opted out of email
                </span>
              )}
            </label>
          );
        })}
      </div>
      <p className="mt-1.5 text-[11px] text-neutral-400" data-testid="recipient-count">
        {selected.length} recipient{selected.length === 1 ? "" : "s"} selected · {eligible.length} of your {leads.length} lead{leads.length === 1 ? "" : "s"} can receive email · only your own CRM leads are listed
      </p>
    </div>
  );
}

function DraftsPanel({ section }) {
  const [drafts, setDrafts] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_DRAFT);
  const [error, setError] = useState("");
  const [social, setSocial] = useState(null);
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishBusy, setPublishBusy] = useState(false);

  const load = () => api.get("/marketing/drafts", { params: { section } }).then((r) => setDrafts(r.data));
  useEffect(() => {
    setDrafts(null);
    load();
    if (section === "social") api.get("/social/status").then((r) => setSocial(r.data)).catch(() => setSocial(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section]);

  const fbConn = (social?.connections || []).find((c) => c.platform === "facebook");

  const publishNow = async () => {
    setPublishBusy(true);
    try {
      await api.post(`/marketing/drafts/${publishTarget.id}/publish`);
      toast.success(`Published to ${fbConn?.display_name || "your Facebook Page"}`);
      setPublishTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setPublishTarget(null);
    } finally {
      setPublishBusy(false);
    }
  };

  const effectiveStatus = (action) => {
    if (action === "schedule") return "Scheduled";
    if (action === "unschedule") return "Draft";
    // plain save keeps the post's current status — editing never unschedules
    return modal.mode === "edit" && modal.draft.status === "Scheduled" ? "Scheduled" : "Draft";
  };

  const buildPayload = (action) => {
    const payload = { title: form.title, content: form.content, section };
    if (section === "email") {
      payload.email_subject = form.email_subject || null;
      payload.recipient_lead_ids = form.recipient_lead_ids;
    }
    if (section === "social") {
      payload.platforms = form.platforms;
      payload.media_ids = form.media.map((m) => m.id);
    }
    if (section === "email" || section === "social") {
      payload.scheduled_at = form.scheduled_at || null;
      payload.timezone = form.timezone || DEVICE_TZ;
      payload.status = effectiveStatus(action);
    }
    return payload;
  };

  const save = async (action = "keep") => {
    setError("");
    const status = effectiveStatus(action);
    if (status === "Scheduled" && section === "email" && form.recipient_lead_ids.length === 0) {
      setError("Select at least one recipient with a valid email address before scheduling.");
      return;
    }
    if (status === "Scheduled" && section === "social" && form.platforms.length === 0) {
      setError("Select at least one social platform before scheduling this post.");
      return;
    }
    if (status === "Scheduled" && !form.scheduled_at) {
      setError("Choose a date and time to schedule.");
      return;
    }
    try {
      const payload = buildPayload(action);
      if (modal.mode === "add") await api.post("/marketing/drafts", payload);
      else await api.put(`/marketing/drafts/${modal.draft.id}`, payload);
      setModal(null);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete draft "${d.title}"?`)) return;
    await api.delete(`/marketing/drafts/${d.id}`);
    load();
  };

  const openEdit = (d) => {
    setForm({
      title: d.title,
      content: d.content || "",
      email_subject: d.email_subject || "",
      recipient_lead_ids: d.recipient_lead_ids || [],
      platforms: d.platforms || [],
      scheduled_at: d.scheduled_at || "",
      timezone: d.timezone || "",
      status: d.status || "Draft",
      media: d.media || [],
    });
    setError("");
    setModal({ mode: "edit", draft: d });
  };

  if (drafts === null) return <p className="py-8 text-center text-sm text-neutral-400">Loading…</p>;

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Btn data-testid={`add-draft-${section}`} onClick={() => { setForm(EMPTY_DRAFT); setError(""); setModal({ mode: "add" }); }}>
          <Plus size={15} /> New Draft
        </Btn>
      </div>
      {drafts.length === 0 ? (
        <Empty testid={`drafts-empty-${section}`} icon={FileText} title="No drafts yet" body="Write your first draft — or let the AI draft it from a one-line brief. Saved to your account." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {drafts.map((d) => (
            <Card key={d.id} className="p-5" data-testid={`draft-card-${d.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2.5">
                  <p className="text-sm font-bold text-[#111111]">{d.title}</p>
                  {d.status === "Scheduled" && d.scheduled_at ? (
                    <Pill tone="black">
                      <span data-testid={`draft-scheduled-${d.id}`}>
                        Scheduled {d.scheduled_at.replace("T", " ").slice(0, 16)}{d.timezone ? ` ${d.timezone}` : ""}
                      </span>
                    </Pill>
                  ) : (
                    <Pill tone="outline"><span data-testid={`draft-status-${d.id}`}>{d.status === "Published" ? "Published" : "Draft"}</span></Pill>
                  )}
                  {d.publish_status === "published" && <Pill tone="green" data-testid={`draft-published-${d.id}`}>Published to Facebook</Pill>}
                  {d.publish_status === "failed" && <Pill tone="red" data-testid={`draft-failed-${d.id}`}>Publish failed</Pill>}
                </div>
                <div className="flex gap-1">
                  <button data-testid={`draft-edit-${d.id}`} aria-label="Edit draft" onClick={() => openEdit(d)} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100">
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`draft-delete-${d.id}`} aria-label="Delete draft" onClick={() => remove(d)} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {section === "email" && (
                <p className="mt-1.5 text-xs text-neutral-500">
                  {d.email_subject ? <span data-testid={`draft-subject-${d.id}`}>Subject: {d.email_subject} · </span> : null}
                  <span data-testid={`draft-recipients-${d.id}`}>{(d.recipient_lead_ids || []).length} recipient{(d.recipient_lead_ids || []).length === 1 ? "" : "s"}</span>
                </p>
              )}
              {section === "social" && (
                <p className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-neutral-500" data-testid={`draft-platforms-${d.id}`}>
                  {(d.platforms || []).length > 0 ? (
                    d.platforms.map((p) => <Pill key={p} tone="outline">{p}</Pill>)
                  ) : (
                    <span className="text-neutral-400">No platforms selected</span>
                  )}
                </p>
              )}
              {section === "social" && (d.media || []).length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`draft-media-${d.id}`}>
                  {d.media.map((m) => (
                    <Pill key={m.id} tone="outline">
                      {m.kind === "video" ? <Film size={10} className="mr-1 inline" /> : <ImagePlus size={10} className="mr-1 inline" />}
                      {m.filename}
                    </Pill>
                  ))}
                </div>
              )}
              <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-xs leading-relaxed text-neutral-600">{d.content || "No content yet."}</p>
              {section === "social" && (d.platforms || []).includes("Facebook") && d.publish_status !== "published" && (
                <button data-testid={`draft-publish-${d.id}`} onClick={() => setPublishTarget(d)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-[#111111]">
                  <Send size={12} /> {d.publish_status === "failed" ? "Retry publish" : "Publish now"}
                </button>
              )}
              {d.publish_status === "failed" && d.publish_error && (
                <p className="mt-1 text-[11px] text-red-500" data-testid={`draft-error-${d.id}`}>{d.publish_error}</p>
              )}
              <p className="mt-3 text-[11px] text-neutral-400">Updated {String(d.updated_at).slice(0, 10)}</p>
            </Card>
          ))}
        </div>
      )}
      {modal && (
        <Modal testid="draft-modal" title={modal.mode === "add" ? "New draft" : "Edit draft"} onClose={() => setModal(null)}>
          <form onSubmit={(e) => { e.preventDefault(); save("keep"); }} className="space-y-4">
            <Field label={section === "email" ? "Internal draft name *" : "Title *"}>
              <input data-testid="draft-title-input" className={inputCls} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
            </Field>
            {section === "email" && (
              <>
                <Field label="Email subject">
                  <input data-testid="draft-subject-input" className={inputCls} placeholder="What recipients see in their inbox" value={form.email_subject} onChange={(e) => setForm((f) => ({ ...f, email_subject: e.target.value }))} />
                </Field>
                <Field label="Recipients / Audience — from your CRM">
                  <RecipientPicker selected={form.recipient_lead_ids} onChange={(ids) => setForm((f) => ({ ...f, recipient_lead_ids: ids }))} />
                </Field>
              </>
            )}
            {section === "social" && (
              <Field label="Platforms">
                <div className="flex flex-wrap gap-2" data-testid="platform-selector">
                  {SOCIAL_PLATFORMS.map((p) => {
                    const on = form.platforms.includes(p);
                    return (
                      <button
                        type="button"
                        key={p}
                        data-testid={`platform-${p.toLowerCase()}`}
                        aria-pressed={on}
                        onClick={() =>
                          setForm((f) => ({ ...f, platforms: on ? f.platforms.filter((x) => x !== p) : [...f.platforms, p] }))
                        }
                        className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                          on ? "border-[#111111] bg-[#111111] text-white" : "border-neutral-300 text-neutral-500 hover:border-[#111111] hover:text-[#111111]"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-1.5 text-[11px] text-neutral-400" data-testid="platform-count">
                  {form.platforms.length} platform{form.platforms.length === 1 ? "" : "s"} selected
                </p>
              </Field>
            )}
            {section === "social" && (
              <Field label="Photos & videos">
                <MediaPicker media={form.media} platforms={form.platforms}
                  onChange={(m) => setForm((f) => ({ ...f, media: m }))} />
              </Field>
            )}
            <AiWriter section={section} onUse={(c) => setForm((f) => ({ ...f, content: c }))} />
            <Field label="Content">
              <textarea data-testid="draft-content-input" rows={8} className={inputCls} value={form.content} onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))} />
            </Field>
            {(section === "email" || section === "social") && (
              <Field label={section === "email" ? "Schedule send (date & time)" : "Schedule post (date & time)"}>
                <input data-testid="draft-schedule-input" type="datetime-local" className={inputCls} value={form.scheduled_at} onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))} />
                <p className="mt-1.5 text-[11px] text-neutral-400" data-testid="schedule-timezone">
                  Timezone: {form.timezone || DEVICE_TZ} (your device)
                </p>
              </Field>
            )}
            {error && <p data-testid="draft-error" className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
            {section === "email" && (
              <div className="flex items-center gap-2.5 rounded-xl bg-neutral-50 px-4 py-3" data-testid="email-send-note">
                <Send size={13} className="shrink-0 text-neutral-400" />
                <p className="text-[11px] leading-relaxed text-neutral-500">
                  Sending is disabled — real email delivery needs a configured email provider plus a consent and
                  unsubscribe workflow (a later phase). Save Draft and Schedule Email only store your plan; nothing is ever sent.
                </p>
              </div>
            )}
            {section === "social" && (
              <div className="flex items-center gap-2.5 rounded-xl bg-neutral-50 px-4 py-3" data-testid="social-publish-note">
                <Send size={13} className="shrink-0 text-neutral-400" />
                <p className="text-[11px] leading-relaxed text-neutral-500">
                  Publishing is disabled — posts go live only after your Instagram, Facebook, LinkedIn or X accounts are
                  connected through an authorised integration (a later phase). A post is only ever marked Published when a
                  connected provider confirms it. Save Draft and Schedule Post only store your plan; nothing is posted.
                </p>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-end gap-2.5">
              <Btn type="button" variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              {modal.mode === "edit" && modal.draft.status === "Scheduled" && (
                <Btn type="button" variant="ghost" data-testid="draft-unschedule-button" onClick={() => save("unschedule")}>
                  Cancel Schedule
                </Btn>
              )}
              {(section === "email" || section === "social") && (
                <>
                  <Btn
                    type="button"
                    data-testid={section === "email" ? "email-send-button" : "social-publish-button"}
                    disabled
                    title={section === "email" ? "Email delivery requires a configured email provider" : "Publishing requires a connected social media account"}
                  >
                    <Send size={14} /> {section === "email" ? "Send Email" : "Publish"}
                  </Btn>
                  <Btn type="button" variant="ghost" data-testid="draft-schedule-button" onClick={() => save("schedule")}>
                    <Clock size={14} /> {section === "email" ? "Schedule Email" : "Schedule Post"}
                  </Btn>
                </>
              )}
              <Btn data-testid="draft-save-button" type="submit">
                {modal.mode === "edit" && modal.draft.status === "Scheduled" ? "Save Changes" : "Save Draft"}
              </Btn>
            </div>
          </form>
        </Modal>
      )}
      {publishTarget && (
        <Modal testid="studio-publish-modal" title="Publish to Facebook" onClose={() => setPublishTarget(null)}>
          {fbConn?.can_publish ? (
            <div className="space-y-3">
              <p className="text-sm text-neutral-600" data-testid="studio-publish-page">
                This publishes the post below to your Facebook Page <span className="font-semibold">{fbConn.display_name}</span> immediately. It cannot be unpublished from here.
              </p>
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3" data-testid="studio-publish-preview">
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">{publishTarget.title}</p>
                <p className="mt-1 line-clamp-6 whitespace-pre-wrap text-sm text-neutral-700">{publishTarget.content || "No caption"}</p>
                {(publishTarget.media || []).length > 0 && (
                  <p className="mt-1 text-xs text-neutral-500">Media attached: {publishTarget.media.map((m) => m.filename).join(", ")} (text-only publishing for now)</p>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Btn variant="ghost" onClick={() => setPublishTarget(null)}>Cancel</Btn>
                <Btn data-testid="studio-publish-confirm" disabled={publishBusy} onClick={publishNow}>
                  {publishBusy ? "Publishing…" : "Yes, publish now"}
                </Btn>
              </div>
            </div>
          ) : (
            <div data-testid="studio-publish-blocked">
              <p className="text-sm text-neutral-600">
                {!fbConn
                  ? "No Facebook Page is connected yet. Connect your Page on the Social Accounts tab first — nothing will be published until then."
                  : "Your Facebook connection doesn't have publishing access yet. Reconnect on the Social Accounts tab to grant it — nothing will be published until you do."}
              </p>
              <div className="mt-4 flex justify-end">
                <Btn variant="ghost" onClick={() => setPublishTarget(null)}>Close</Btn>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function CampaignsPanel() {
  const [campaigns, setCampaigns] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_CAMPAIGN);
  const [error, setError] = useState("");

  const load = () => api.get("/marketing/campaigns").then((r) => setCampaigns(r.data));
  useEffect(() => {
    load();
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const openEdit = (c) => {
    setForm({
      name: c.name,
      objective: c.objective || "",
      audience: c.audience || "",
      channel: c.channel || "Email",
      content: c.content || "",
      scheduled_date: c.scheduled_date || "",
      status: c.status || "Draft",
    });
    setError("");
    setModal({ mode: "edit", campaign: c });
  };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    const payload = { ...form, scheduled_date: form.scheduled_date || null };
    try {
      if (modal.mode === "edit") await api.put(`/marketing/campaigns/${modal.campaign.id}`, payload);
      else await api.post("/marketing/campaigns", payload);
      setModal(null);
      setForm(EMPTY_CAMPAIGN);
      load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    await api.delete(`/marketing/campaigns/${c.id}`);
    load();
  };

  if (campaigns === null) return <p className="py-8 text-center text-sm text-neutral-400">Loading…</p>;

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Btn data-testid="add-marketing-campaign-button" onClick={() => { setForm(EMPTY_CAMPAIGN); setError(""); setModal({ mode: "add" }); }}>
          <Plus size={15} /> New Campaign
        </Btn>
      </div>
      {campaigns.length === 0 ? (
        <Empty testid="marketing-campaigns-empty" icon={Megaphone} title="No campaigns yet" body="Create a campaign with an objective, audience, channel and schedule. Nothing is sent or published — integrations come later." />
      ) : (
        <div className="space-y-3">
          {campaigns.map((c) => (
            <Card key={c.id} className="p-5" data-testid={`marketing-campaign-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2.5">
                    <p className="text-sm font-bold text-[#111111]">{c.name}</p>
                    <Pill tone={c.status === "Scheduled" ? "black" : "outline"}>{c.status}</Pill>
                  </div>
                  <p className="mt-1 text-xs text-neutral-500">
                    {c.channel}{c.objective ? ` · ${c.objective}` : ""}{c.audience ? ` · Audience: ${c.audience}` : ""}{c.scheduled_date ? ` · Scheduled ${c.scheduled_date}` : ""}
                  </p>
                  {c.content && <p className="mt-2 line-clamp-2 whitespace-pre-wrap text-xs text-neutral-600">{c.content}</p>}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button data-testid={`campaign-edit-${c.id}`} aria-label="Edit campaign" onClick={() => openEdit(c)} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-neutral-100">
                    <Pencil size={14} />
                  </button>
                  <button data-testid={`marketing-campaign-delete-${c.id}`} aria-label="Delete campaign" onClick={() => remove(c)} className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-500 hover:bg-red-50 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      {modal && (
        <Modal testid="marketing-campaign-modal" title={modal.mode === "edit" ? "Edit campaign" : "New marketing campaign"} onClose={() => setModal(null)}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Campaign name *">
              <input data-testid="mcampaign-name-input" className={inputCls} value={form.name} onChange={set("name")} required />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Objective">
                <input data-testid="mcampaign-objective-input" className={inputCls} value={form.objective} onChange={set("objective")} placeholder="e.g. Re-engage old leads" />
              </Field>
              <Field label="Target audience">
                <input data-testid="mcampaign-audience-input" className={inputCls} value={form.audience} onChange={set("audience")} placeholder="e.g. Leads not contacted in 30 days" />
              </Field>
              <Field label="Channel">
                <select data-testid="mcampaign-channel-select" className={inputCls} value={form.channel} onChange={set("channel")}>
                  {["Email", "SMS", "Social Media", "Paid Ads"].map((c) => <option key={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Scheduled date">
                <input data-testid="mcampaign-date-input" type="date" className={inputCls} value={form.scheduled_date} onChange={set("scheduled_date")} />
              </Field>
            </div>
            <AiWriter section="campaign" onUse={(c) => setForm((f) => ({ ...f, content: c }))} />
            <Field label="Draft content">
              <textarea data-testid="mcampaign-content-input" rows={5} className={inputCls} value={form.content} onChange={set("content")} />
            </Field>
            <Field label="Status">
              <select data-testid="mcampaign-status-select" className={inputCls} value={form.status} onChange={set("status")}>
                <option>Draft</option>
                <option>Scheduled</option>
              </select>
            </Field>
            {error && <p className="rounded-xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2.5">
              <Btn type="button" variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
              <Btn data-testid="mcampaign-save-button" type="submit">{modal.mode === "edit" ? "Save Changes" : "Save Campaign"}</Btn>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const CAL_STATUS_STYLE = {
  Scheduled: "bg-[#111111] text-white",
  Published: "bg-green-100 text-green-800 border border-green-300",
  Failed: "bg-red-100 text-red-700 border border-red-300",
};
const CAL_KINDS = ["Email", "Social", "Campaign"];

function mondayOf(d) {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  x.setHours(0, 0, 0, 0);
  return x;
}

function CalendarPanel() {
  const [items, setItems] = useState(null);
  const [openDraft, setOpenDraft] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(() => new Date());
  const [kinds, setKinds] = useState({ Email: true, Social: true, Campaign: true });
  useEffect(() => {
    Promise.all([
      api.get("/marketing/campaigns"),
      api.get("/marketing/drafts", { params: { section: "email" } }),
      api.get("/marketing/drafts", { params: { section: "social" } }),
    ]).then(([c, em, so]) => {
      const draftStatus = (x) =>
        x.publish_status === "published" ? "Published" : x.publish_status === "failed" ? "Failed" : "Scheduled";
      const scheduledDraft = (x, label) => ({
        id: `${label.toLowerCase()}-${x.id}`,
        draftId: label === "Social" && x.content_plan_id ? x.id : null,
        format: x.format || null,
        name: x.title,
        kind: label,
        detail: label === "Social" ? (x.platforms || []).join(", ") : x.email_subject || "",
        status: draftStatus(x),
        when: x.scheduled_at.slice(0, 10),
        time: x.scheduled_at.slice(11, 16),
        timezone: x.timezone || "",
      });
      const merged = [
        ...c.data
          .filter((x) => x.scheduled_date)
          .map((x) => ({ id: x.id, name: x.name, kind: "Campaign", detail: x.channel || "", status: x.status === "Published" ? "Published" : "Scheduled", when: x.scheduled_date, time: "", timezone: "" })),
        ...em.data.filter((x) => x.status === "Scheduled" && x.scheduled_at).map((x) => scheduledDraft(x, "Email")),
        ...so.data
          .filter((x) => (x.status === "Scheduled" || x.status === "Published" || x.publish_status === "failed") && x.scheduled_at)
          .map((x) => scheduledDraft(x, "Social")),
      ].sort((a, b) => `${a.when}T${a.time || "00:00"}`.localeCompare(`${b.when}T${b.time || "00:00"}`));
      setItems(merged);
    });
  }, [reloadKey]);

  if (items === null) return <p className="py-8 text-center text-sm text-neutral-400">Loading…</p>;
  const shown = items.filter((i) => kinds[i.kind]);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const itemsOn = (dayIso) => shown.filter((i) => i.when === dayIso);

  const move = (dir) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + 7 * dir);
    setCursor(d);
  };

  const openItem = async (c) => {
    if (!c.draftId) return;
    try {
      const { data } = await api.get(`/marketing/content-drafts/${c.draftId}`);
      setOpenDraft(data);
    } catch { /* draft no longer exists */ }
  };

  const Chip = ({ c }) => (
    <button type="button" onClick={() => openItem(c)}
      className={`block w-full truncate rounded-md px-1.5 py-0.5 text-left text-[10px] font-semibold ${CAL_STATUS_STYLE[c.status] || CAL_STATUS_STYLE.Scheduled} ${c.draftId ? "cursor-pointer" : "cursor-default"}`}
      data-testid={`cal-chip-${c.id}`} title={`${c.name} — ${c.kind} · ${c.status}${c.time ? ` · ${c.time}` : ""}`}>
      {c.time ? `${c.time} ` : ""}{c.name}
    </button>
  );

  const weekStart = mondayOf(cursor);
  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = view === "month" ? mondayOf(monthStart) : weekStart;
  const gridDays = Array.from({ length: view === "month" ? 42 : 7 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const title = view === "month"
    ? cursor.toLocaleString("en-GB", { month: "long", year: "numeric" })
    : `Week of ${weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;

  return (
    <div data-testid="calendar-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Btn variant="ghost" data-testid="cal-prev" onClick={() => move(-1)}>‹</Btn>
          <Btn variant="ghost" data-testid="cal-today" onClick={() => setCursor(new Date())}>Today</Btn>
          <Btn variant="ghost" data-testid="cal-next" onClick={() => move(1)}>›</Btn>
          <p className="ml-2 text-sm font-bold text-[#111111]" data-testid="cal-title">{title}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs" data-testid="cal-filters">
            {CAL_KINDS.map((k) => (
              <label key={k} className="flex cursor-pointer items-center gap-1 font-medium text-neutral-600">
                <input type="checkbox" className="h-3.5 w-3.5 accent-neutral-900" data-testid={`cal-filter-${k.toLowerCase()}`}
                  checked={kinds[k]} onChange={(e) => setKinds((f) => ({ ...f, [k]: e.target.checked }))} />
                {k}
              </label>
            ))}
          </div>
          <div className="flex rounded-full border border-neutral-300 p-0.5" data-testid="cal-view-switch">
            {["month", "week", "list"].map((v) => (
              <button key={v} data-testid={`cal-view-${v}`} onClick={() => setView(v)}
                className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${view === v ? "bg-[#111111] text-white" : "text-neutral-500"}`}>
                {v}
              </button>
            ))}
          </div>
        </div>
      </div>

      {shown.length === 0 ? (
        <Empty testid="calendar-empty" icon={CalendarDays} title="Nothing scheduled" body="Approved AI content-plan posts, scheduled campaigns and email drafts will appear here in date order." />
      ) : view === "list" ? (
        <div className="space-y-3" data-testid="calendar-list">
          {shown.map((c) => (
            <Card key={c.id}
              className={`flex items-center gap-4 p-4 ${c.draftId ? "cursor-pointer transition-colors hover:border-neutral-400" : ""}`}
              data-testid={`calendar-item-${c.id}`} onClick={() => openItem(c)}>
              <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-xl bg-[#111111] text-white">
                <span className="text-sm font-extrabold leading-none">{c.when.slice(8, 10)}</span>
                <span className="text-[9px] font-semibold uppercase">{new Date(`${c.when}T00:00:00`).toLocaleString("en-GB", { month: "short" })}</span>
              </div>
              <div>
                <p className="text-sm font-bold text-[#111111]">{c.name}</p>
                <p className="text-xs text-neutral-500">
                  {c.kind}{c.detail ? ` — ${c.detail}` : ""}{c.format ? ` · ${c.format}` : ""} · {c.status}{c.time ? ` · ${c.time}${c.timezone ? ` ${c.timezone}` : ""}` : ""}{c.status === "Scheduled" ? ` · stored schedule, not ${c.kind === "Social" ? "posted" : "sent"}` : ""}
                </p>
              </div>
              {c.draftId && <Pencil size={14} className="ml-auto shrink-0 text-neutral-300" />}
            </Card>
          ))}
        </div>
      ) : (
        <Card className="overflow-hidden p-0" data-testid="calendar-grid">
          <div className="grid grid-cols-7 border-b border-neutral-200 bg-neutral-50 text-center text-[10px] font-bold uppercase tracking-wider text-neutral-500">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {gridDays.map((d) => {
              const dayIso = iso(d);
              const dayItems = itemsOn(dayIso);
              const isToday = dayIso === iso(new Date());
              const inMonth = view === "week" || d.getMonth() === cursor.getMonth();
              return (
                <div key={dayIso} data-testid={`cal-day-${dayIso}`}
                  className={`min-h-[92px] border-b border-r border-neutral-100 p-1.5 ${inMonth ? "bg-white" : "bg-neutral-50/60"}`}>
                  <p className={`mb-1 text-right text-[11px] font-semibold ${isToday ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#111111] text-white float-right" : inMonth ? "text-neutral-700" : "text-neutral-300"}`}>
                    {d.getDate()}
                  </p>
                  <div className="clear-both space-y-1">
                    {dayItems.slice(0, 3).map((c) => <Chip key={c.id} c={c} />)}
                    {dayItems.length > 3 && <p className="text-[10px] font-semibold text-neutral-400">+{dayItems.length - 3} more</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      {openDraft && (
        <EditDraftModal draft={openDraft} onClose={() => setOpenDraft(null)}
          onSaved={() => { setOpenDraft(null); setReloadKey((k) => k + 1); }} />
      )}
    </div>
  );
}

function MarketingAnalytics() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/analytics/summary").then((r) => setS(r.data));
  }, []);
  if (!s) return <p className="py-8 text-center text-sm text-neutral-400">Loading…</p>;
  const rows = [
    { label: "Social content items", value: s.drafts_by_section.social },
    { label: "Email content items", value: s.drafts_by_section.email },
    { label: "Ad copy items", value: s.drafts_by_section.ads },
    { label: "Campaigns", value: s.campaigns },
  ];
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="space-y-4" data-testid="marketing-analytics">
      <Card className="p-6">
        <p className="mb-5 text-xs font-bold uppercase tracking-wider text-neutral-500">Your real content counts</p>
        <div className="space-y-3.5">
          {rows.map((r) => (
            <div key={r.label}>
              <div className="mb-1 flex justify-between text-xs">
                <span className="font-medium text-neutral-600">{r.label}</span>
                <span className="font-bold text-[#111111]">{r.value}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-100">
                <div className="h-full rounded-full bg-[#111111] transition-all" style={{ width: `${(r.value / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </Card>
      <Notice testid="marketing-analytics-notice" title="Send & publish metrics not connected" body="Opens, clicks and impressions will appear once email, SMS and social integrations are live. No marketing performance is simulated." />
    </div>
  );
}

export default function Marketing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab = TABS.some((t) => t.id === requested) ? requested : "social";
  const setTab = (id) => setSearchParams({ tab: id });

  return (
    <div data-testid="marketing-page">
      <PageHeader title="AI Marketing Studio" sub="Draft content yourself or generate it with AI, organise campaigns and schedule them. Nothing is sent or published — those integrations connect later." />

      <div className="mb-6 flex flex-wrap gap-1.5 rounded-2xl bg-neutral-100 p-1.5" role="tablist" data-testid="marketing-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            data-testid={`marketing-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`rounded-xl px-3.5 py-2 text-xs font-semibold transition-all sm:text-sm ${
              tab === t.id ? "bg-white text-[#111111] shadow-sm" : "text-neutral-500 hover:text-[#111111]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "strategy" && <StrategyPanel />}
      {tab === "plan" && <ContentPlanPanel />}
      {tab === "email" && <EmailPanel />}
      {tab === "whatsapp" && <WhatsAppPanel />}
      {tab === "ads" && <AdvertisingPanel />}
      {tab === "social" && <DraftsPanel section={tab} />}
      {tab === "campaigns" && <CampaignsPanel />}
      {tab === "calendar" && <CalendarPanel />}
      {tab === "seo" && <SEOPanel />}
      {tab === "landing-pages" && <LandingPagesPanel />}
      {tab === "analytics-studio" && <StudioAnalyticsPanel />}
      {tab === "social-accounts" && <SocialAccountsPanel />}
      {tab === "followups" && <><SequencesPanel /><TasksPanel /></>}
      {tab === "analytics" && <MarketingAnalytics />}
    </div>
  );
}
